import type { RigidSystem } from './jacobian';
import { evaluateRigidSystem } from './jacobian';
import { matmul, transpose, solveSPD, matTvec, matvec, type Matrix } from '@/geometry/linalg';
import { MAX_FOLD_ANGLE, clamp } from '@/constants/numeric';

/**
 * One damped Gauss-Newton step of the rigid-folding projection method:
 *
 *     step = -J^+ C  +  (I - J^+ J) desired ,     J^+ = J^T (J J^T + lambda I)^-1
 *
 * The first term walks back onto the constraint variety C(rho) = 0; the second
 * moves in the direction the user asked for, restricted to the variety's tangent
 * space. Damping (Levenberg-Marquardt) keeps the step bounded at the bifurcation
 * points that flat-foldable patterns are full of.
 *
 * Returns null when the damped normal equations cannot be factorised even after
 * the ridge is raised - the caller must report that, not paper over it.
 */
export function projectedNewtonStep(
  J: Matrix,
  C: Float64Array,
  desired: Float64Array,
  damping: number,
): Float64Array | null {
  const Jt = transpose(J);
  const JJt = matmul(J, Jt);
  const m = JJt.rows;
  if (m === 0) return Float64Array.from(desired);

  let trace = 0;
  for (let i = 0; i < m; i++) trace += JJt.data[i * m + i];
  const lambda = damping * (trace / Math.max(1, m) + 1e-12);

  const yC = solveSPD(JJt, C, lambda);
  if (!yC) return null;
  const correction = matTvec(J, yC);

  const Jd = matvec(J, desired);
  const yD = solveSPD(JJt, Jd, lambda);
  if (!yD) return null;
  const proj = matTvec(J, yD);

  const out = new Float64Array(J.cols);
  for (let i = 0; i < J.cols; i++) out[i] = -correction[i] + (desired[i] - proj[i]);
  return out;
}

/** Newton-project rho back onto the constraint variety, in place. */
export function projectOntoVariety(
  system: RigidSystem,
  rho: Float64Array,
  iterations = 12,
  damping = 1e-8,
  tolerance = 1e-13,
): number {
  let residual = Infinity;
  const zero = new Float64Array(system.variableCount);
  for (let i = 0; i < iterations; i++) {
    const { residual: C, jacobian, maxResidual } = evaluateRigidSystem(system, rho, true);
    residual = maxResidual;
    if (residual < tolerance) break;
    const step = projectedNewtonStep(jacobian, C, zero, damping);
    if (!step) break;
    let moved = 0;
    for (let k = 0; k < rho.length; k++) {
      rho[k] = clamp(rho[k] + step[k], -MAX_FOLD_ANGLE, MAX_FOLD_ANGLE);
      moved = Math.max(moved, Math.abs(step[k]));
    }
    if (moved < 1e-16) break;
  }
  return residual;
}

/**
 * Walk a short way along the folding branch in the direction `direction`, then
 * return the resulting fold angles.
 *
 * This exists because the completely unfolded state is a bifurcation point: at
 * rho = 0 every rotation is the identity, so dM/drho_k = [u_k]x and the third
 * row of every vertex block vanishes identically. Measuring the rank there gives
 * the *first-order* (infinitesimal) mobility, which is generically larger than
 * the mobility of the actual folding branch. Measuring on the branch is the
 * number a user means when they ask "how many degrees of freedom does this
 * mechanism have?".
 */
export function advanceAlongBranch(
  system: RigidSystem,
  direction: Float64Array,
  amount = 0.25,
  iterations = 40,
  damping = 1e-6,
): { rho: Float64Array; residual: number; reached: number } {
  const rho = new Float64Array(system.variableCount);
  if (system.variableCount === 0) return { rho, residual: 0, reached: 0 };

  const target = new Float64Array(system.variableCount);
  for (let i = 0; i < target.length; i++) {
    target[i] = clamp(direction[i] * amount, -MAX_FOLD_ANGLE, MAX_FOLD_ANGLE);
  }

  if (system.constraintCount === 0) {
    rho.set(target);
    return { rho, residual: 0, reached: amount };
  }

  const stepScale = 0.4;
  let residual = Infinity;
  for (let it = 0; it < iterations; it++) {
    const { residual: C, jacobian, maxResidual } = evaluateRigidSystem(system, rho, true);
    residual = maxResidual;
    const desired = new Float64Array(system.variableCount);
    for (let i = 0; i < desired.length; i++) {
      desired[i] = (target[i] - rho[i]) * stepScale;
    }
    const step = projectedNewtonStep(jacobian, C, desired, damping);
    if (!step) break;
    let moved = 0;
    for (let i = 0; i < rho.length; i++) {
      rho[i] = clamp(rho[i] + step[i], -MAX_FOLD_ANGLE, MAX_FOLD_ANGLE);
      moved = Math.max(moved, Math.abs(step[i]));
    }
    if (moved < 1e-15) break;
  }
  residual = projectOntoVariety(system, rho, 20, damping * 0.01);

  let reached = 0;
  for (const r of rho) reached = Math.max(reached, Math.abs(r));
  return { rho, residual, reached };
}
